import React, { type FC, Fragment } from "react";
import clsx from "clsx";
import Swatch from "../../../../../../../shared-components/css-modules/lexus/atoms/Swatch";
import baseStyles from "../../Compare.module.scss";
import styles from "./Styling.module.scss";
import a11y from "../../../../../../../shared-components/css-modules/lexus/utils/Accessibility.module.scss";
import { getDetailedGrade, isSingle } from "../../compareUtils";
import { selectCompareSpecs } from "../../../../../../redux/slices/CarConfigCompareSpecsSlice";
import { useCarConfigSelector } from "../../../../../../redux/store";
import { useGetCompareGrades } from "../../compareHooks";
import { getCarDBAssetUrl } from "../../../../../../../../shared-logic/utils/imageUtils";
import SectionCollapse from "../SectionCollapse";
import { CollapseSection, trackExpandedItem } from "../../../../../../utils/tracking";
import { useCompareContext } from "../../CompareContext";
import { CarConfigLabel, useCarConfigLabel } from "../../../../../../utils/labelUtils";

const Styling: FC = () => {
    const { openedCollapses, setOpenedCollapses } = useCompareContext();
    const grades = useGetCompareGrades();
    const compareSpecsGrades = useCarConfigSelector(selectCompareSpecs);
    const compressedCcisPath = useCarConfigSelector((state) => state.commonSettings.compressedCcisPath);

    const carConfigLbxCompareStyling = useCarConfigLabel("carConfigLbxCompareStyling");

    return (
        <SectionCollapse
            open={openedCollapses.includes(CollapseSection.SectionStyling)}
            onChange={() => {
                const isOpen = openedCollapses.includes(CollapseSection.SectionStyling);

                trackExpandedItem(isOpen, CollapseSection.SectionStyling);

                setOpenedCollapses(
                    isOpen
                        ? openedCollapses.filter((item) => item !== CollapseSection.SectionStyling)
                        : [...openedCollapses, CollapseSection.SectionStyling],
                );
            }}
            summary={carConfigLbxCompareStyling}
            fullContentWidth
        >
            <div className={clsx(styles.styling, isSingle(grades) ? styles.empty : undefined)}>
                {/* Exterior colours */}
                {grades.map((grade, index) => {
                    const detailedGrade = getDetailedGrade(compareSpecsGrades, grade);
                    return (
                        // This array is fixed, we can use index here
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={index}>
                            <div className={styles["animation-wrapper"]}>
                                {grade && detailedGrade && (
                                    <>
                                        <h4 className={baseStyles["compare-section-title"]}>
                                            <CarConfigLabel label="carConfigLbxCompareExterior" />
                                        </h4>
                                        <div className={styles.swatches}>
                                            {detailedGrade.colors.map((color) => (
                                                <Fragment key={color.id}>
                                                    <Swatch variant="sm" fill={`#${color.rgb}`} aria-hidden="true" />
                                                    <span className={a11y["sr-only"]}>{color.name}</span>
                                                </Fragment>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}

                {/* Interior colours */}
                {grades.map((grade, index) => {
                    const detailedGrade = getDetailedGrade(compareSpecsGrades, grade);
                    return (
                        // This array is fixed, we can use index here
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={index}>
                            <div className={styles["animation-wrapper"]}>
                                {grade && detailedGrade && (
                                    <>
                                        <h4 className={baseStyles["compare-section-title"]}>
                                            <CarConfigLabel label="carConfigLbxCompareInterior" />
                                        </h4>
                                        <div className={styles.swatches}>
                                            {detailedGrade.upholsteries.map((upholstery) => (
                                                <Fragment key={upholstery.id}>
                                                    <Swatch
                                                        variant="sm"
                                                        image={getCarDBAssetUrl(
                                                            upholstery.image,
                                                            compressedCcisPath,
                                                            128,
                                                            128,
                                                        )}
                                                        aria-hidden="true"
                                                    />
                                                    <span className={a11y["sr-only"]}>{upholstery.name}</span>
                                                </Fragment>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}

                {grades.map((grade, index) => (
                    // This array is fixed, we can use index here
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={index}>
                        <div className={styles["animation-wrapper"]}>
                            {grade && (
                                <>
                                    <h4 className={baseStyles["compare-section-title"]}>
                                        <CarConfigLabel label="carConfigLbxCompareFabric" />
                                    </h4>
                                    <div className={styles.fabric}>{grade.upholstery.name}</div>
                                </>
                            )}
                        </div>
                    </div>
                ))}

                {grades.map((grade, index) => (
                    // This array is fixed, we can use index here
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={index}>
                        <div className={styles["animation-wrapper"]}>
                            {grade && (
                                <>
                                    <h4 className={baseStyles["compare-section-title"]}>
                                        <CarConfigLabel label="carConfigLbxCompareAlloys" />
                                    </h4>
                                    <div className={styles.alloy}>
                                        {grade.wheel.image && (
                                            <img src={grade.wheel.image} alt="" role="presentation" />
                                        )}
                                        {grade.wheel.name}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </SectionCollapse>
    );
};
export default Styling;
